/**
 * Which element a tour step points at, resolved against the current viewport (CNCT-6771).
 *
 * design-contract.md → §Surface 3.2 (indicator placement is anchored to the step's subject; the
 * narration card stays fixed).
 *
 * Below Tailwind's `lg` breakpoint the left-nav rail is `display:none` and lives behind the header
 * hamburger. A step that rings a nav row there would either draw over empty space or narrate an
 * element the user cannot see, so on those viewports every left-nav step retargets to the hamburger
 * and re-labels its eyebrow accordingly. Pure and viewport-width-driven, so the card and the indicator
 * layer stay in agreement and both simply re-resolve on resize.
 */

import { ArrowSide } from './tour-position.util'

/** Tailwind `lg`. At or above this width the left-nav rail is rendered; below it, the hamburger owns it. */
export const LEFT_NAV_MIN_WIDTH = 1024

/** `data-tour-id` of the header hamburger that opens the nav drawer on narrow viewports. */
export const HAMBURGER_ANCHOR = 'nav-hamburger'

/** Eyebrow used in place of "Left navigation" once the rail is behind the hamburger. */
export const HAMBURGER_CATEGORY = 'Main menu'

/** The subset of a guide step this resolution reads. `IGuideStep` satisfies it structurally. */
export interface ITourTargetSpec {
  /** `data-tour-id` of the element the ring + arrow sit on. Absent on the intro step (no indicator). */
  anchor?: string
  /** Preferred side of the anchor for the arrow. */
  arrow?: ArrowSide
  /** Eyebrow shown above the card title. */
  category?: string
  /** True when `anchor` lives in the left-nav rail, i.e. it disappears below `lg`. */
  leftNav?: boolean
  /**
   * Where this step points when `anchor` is not rendered below `lg`. The header's tour compass is
   * the case: it is desktop-only, and its job moves to the account menu's own "Take a tour" — a
   * different element in different chrome, so it needs its own arrow side and, where the chrome has
   * to be opened first, its own `accountMenu`. Distinct from `leftNav`, which always retargets to
   * the hamburger.
   */
  narrow?: { anchor: string; arrow?: ArrowSide; category?: string; accountMenu?: boolean }
}

/** The element to indicate on this viewport, with the arrow side and eyebrow that go with it. */
export interface IResolvedTourTarget {
  /** `null` when the step shows no indicator at all (the intro). */
  anchor: string | null
  arrow: ArrowSide
  category?: string
}

/** Whether the left-nav rail is rendered at `viewportWidth` (vs. hidden behind the hamburger). */
export function isLeftNavVisible(viewportWidth: number): boolean {
  return viewportWidth >= LEFT_NAV_MIN_WIDTH
}

/**
 * The step's effective indicator target at `viewportWidth`. A left-nav step on a narrow viewport
 * retargets to the hamburger (pointed at from below, eyebrow "Main menu"); everything else keeps its
 * declared anchor, defaulting the arrow to the right of it.
 */
export function resolveTourTarget(spec: ITourTargetSpec, viewportWidth: number): IResolvedTourTarget {
  if (!spec.anchor) return { anchor: null, arrow: spec.arrow ?? 'right', category: spec.category }
  if (spec.leftNav && !isLeftNavVisible(viewportWidth)) {
    return { anchor: HAMBURGER_ANCHOR, arrow: 'bottom', category: HAMBURGER_CATEGORY }
  }
  if (spec.narrow && !isLeftNavVisible(viewportWidth)) {
    return { anchor: spec.narrow.anchor, arrow: spec.narrow.arrow ?? 'left', category: spec.narrow.category ?? spec.category }
  }
  return { anchor: spec.anchor, arrow: spec.arrow ?? 'right', category: spec.category }
}

/** Whether this step needs the account menu open at `viewportWidth` — its own, or its narrow stand-in's. */
export function needsAccountMenu(spec: ITourTargetSpec & { accountMenu?: boolean }, viewportWidth: number): boolean {
  if (spec.narrow && !isLeftNavVisible(viewportWidth)) return !!spec.narrow.accountMenu
  return !!spec.accountMenu
}
